import * as Sentry from "@sentry/react";
import moment from "moment";

import { createLocalAxios } from "../../lib/api.js";
import apiMessages from "../../lib/apiMessages.js";

import {
  loadingTrainStopSignalImages,
  loadingTrainStopSignalImagesDone,
  setTrainStopSignalImages,
  loadingStations,
  loadingStationsDone,
  setStations,
} from "./slice.js";

export const fetchTrainStopSignalImages =
  (params) => async (dispatch, getState) => {
    const { page, pageSize, search } = params || {};
    dispatch(loadingTrainStopSignalImages());

    const localAxios = createLocalAxios(getState);
    const query = new URLSearchParams();
    if (page !== undefined) query.set("page", page);
    if (pageSize !== undefined) query.set("page_size", pageSize);
    if (search) query.set("search", search);

    let response;
    try {
      response = await localAxios.get(
        `machinist/train-stops-signal-images?${query.toString()}`,
      );
    } catch (error) {
      console.error("Error while fetching train stop signal images:", error);
      Sentry.captureException(error);
      throw error;
    } finally {
      dispatch(loadingTrainStopSignalImagesDone());
    }

    dispatch(setTrainStopSignalImages(response.data.trainStopSignalImages));
    return response.data.pages;
  };

export const exportSignalImagesReport =
  (date) => async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);
    const url = "machinist/signal-images/export";
    const query = new URLSearchParams({
      date: moment(date).format("YYYY-MM-DD"),
    });

    let response;
    try {
      response = await localAxios.get(`${url}?${query.toString()}`, {
        responseType: "blob",
      });
    } catch (error) {
      console.error("Error while exporting signal images:", error);
      Sentry.captureException(error);
      throw error;
    }

    return response;
  };

export const saveTrainStopSignalImage =
  (stopCode, stationName, signalImageRequired, directions = []) =>
  async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);
    const url = "machinist/train-stop";
    const body = { stopCode, stationName, signalImageRequired, directions };

    let response;
    try {
      response = await localAxios.patch(url, body);
    } catch (error) {
      console.error("Error while creating a new train stop:", error);
      Sentry.captureException(
        new Error(
          `Something went wrong: saveTrainStopSignalImage post-> ${error}`,
        ),
      );
      throw error;
    }

    if (response.data.message !== apiMessages.MSG_SUCCESS) {
      throw new Error(response.data.message);
    }

    return response;
  };

export const fetchTrainStationData = (params) => async (dispatch, getState) => {
  const { search } = params || {};
  if (search.length < 2) return;
  dispatch(loadingStations());

  const localAxios = createLocalAxios(getState);
  const query = new URLSearchParams();
  if (search) query.set("search", search);

  let response;

  try {
    response = await localAxios.get(`machinist/start-station/${search}`);
  } catch (error) {
    console.error("Error while fetching stations:", error);
    Sentry.captureException(error);
    throw error;
  } finally {
    dispatch(loadingStationsDone());
  }

  dispatch(setStations(response.data));
  return response.data;
};

/**
 * Searches stations without storing them, e.g. for selecting directions.
 */
export const searchStations = (search) => async (dispatch, getState) => {
  if (!search || search.length < 2) return [];

  const localAxios = createLocalAxios(getState);

  let response;
  try {
    response = await localAxios.get(
      `machinist/start-station/${encodeURIComponent(search)}`,
    );
  } catch (error) {
    if (error.response?.status === 404) return [];
    console.error("Error while searching stations:", error);
    Sentry.captureException(error);
    throw error;
  }

  return Array.isArray(response.data) ? response.data : [];
};

export const deleteTrainStopSignalImage =
  (stopCode) => async (dispatch, getState) => {
    dispatch(loadingStations());
    const localAxios = createLocalAxios(getState);
    const url = `machinist/train-stop/${stopCode}`;

    let response;

    try {
      response = await localAxios.delete(url);
    } catch (error) {
      console.error("Error while deleting train stop:", error);

      Sentry.captureException(
        new Error(
          `Something went wrong: deleteTrainStopSignalImage delete -> ${error}`,
        ),
      );

      throw error;
    } finally {
      dispatch(loadingStationsDone());
    }

    return response;
  };
